import DOMPurify from 'dompurify'
import { get } from '../../vue_shared/http'
import helpers from '../helpers'
import { getPrizmdocBaseUrl } from '../../vue_shared/api'
import _ from 'lodash'
import { getViewerAssetsPath } from './viewer-assets-path'

// prizmdoc requirements
// see https://github.com/Accusoft/prizmdoc-viewer#setup-your-html-head
import './viewer-assets/css/normalize.min.css'
import './viewer-assets/css/viewer.css'
// jquery itself is already loaded, we use the existing one
import './viewer-assets/js/jquery.hotkeys.min.js'
import './viewer-assets/js/underscore.min.js'
import './viewer-assets/js/viewercontrol.js'
import './viewer-assets/js/viewer.js'
import './viewer-assets/js/viewerCustomizations.js'

// our styling
import './colors.scss'
import './nextrequest.scss'

function isAgencyStaff(currentUser) {
  const user = currentUser || getUserFromHeader()

  return user?.user_role !== 'guest'
}

function getUserFromHeader() {
  const el = document.querySelector(
    '#app-header-legacy .mount'
  ) as HTMLElement | null

  try {
    return el?.dataset.user ? JSON.parse(el.dataset.user) : undefined
  } catch {
    return undefined
  }
}

async function fetchRegexes(currentUser) {
  if (!isAgencyStaff(currentUser)) {
    return undefined
  }
  const response = (await get('/admin/redaction_regexes')) as any
  const regexes = response?.data
  const redactionRegexes = {
    highlightColor: '#ffff00',
    searchOnInit: false,
    globalOptions: {
      matchCase: false,
      endsWith: false,
      beginsWith: false,
      matchWholeWord: false,
      exactPhrase: false
    },
    terms: []
  }
  if (regexes) {
    // eslint-disable-next-line camelcase
    redactionRegexes.terms = regexes.map(({ regex_name, regex }) => ({
      searchTermIsRegex: true,
      // eslint-disable-next-line camelcase
      searchTerm: regex_name,
      // eslint-disable-next-line camelcase
      description: regex_name,
      userDefinedRegex: regex,
      highlightColor: '#ffff00',
      selected: false
    }))
  }
  return redactionRegexes
}

async function fetchRedactionReasons(): Promise<any> {
  return get('/admin/redaction_reasons').then(({ data: reasons }: any) => ({
    autoApplyDefaultReason: true,
    enableRedactionReasonSelection: true,
    enableFreeformRedactionReasons: true,
    maxLengthFreeformRedactionReasons: 80,
    reasons:
      reasons?.length > 0
        ? reasons.map(({ name }) => ({
            reason: _.escape(name)
          }))
        : [
            { reason: 'Client Privilege' },
            { reason: 'Privacy Information' },
            { reason: 'Redacted' }
          ]
  }))
}

async function openRedactionTab(): Promise<any> {
  const redactTabItem = document.querySelector('[data-pcc-nav-tab=redact]')
  if (redactTabItem) {
    redactTabItem.classList.add('pcc-active')
    const pccTabPane = redactTabItem.querySelector('.pcc-tab-pane')
    if (pccTabPane) {
      pccTabPane.classList.add('pcc-open')
    }
  }
}

async function closeViewTab(): Promise<any> {
  const viewTabItem = document.querySelector('[data-pcc-nav-tab=view]')
  if (viewTabItem) {
    viewTabItem.classList.remove('pcc-active')
    const pccTabPane = viewTabItem.querySelector('.pcc-tab-pane')
    if (pccTabPane) {
      pccTabPane.classList.remove('pcc-open')
    }
  }
}

async function showRedactionTab(selector: string): Promise<any> {
  // used for redaction show
  $(selector).on('ViewerReady', function () {
    openRedactionTab()
    closeViewTab()
  })
  // used for rapid review
  // @ts-ignore: pccViewer is defined in `prizmdoc/viewer.js`.
  if ($(selector).pccViewer()) {
    openRedactionTab()
    closeViewTab()
  }
}

export default async function PrizmdocViewer(currentUser = null): Promise<any> {
  const [regexes, redactionReasons] = await Promise.all([
    fetchRegexes(currentUser),
    fetchRedactionReasons()
  ])

  return async (
    selector: string,
    viewingSessionId: string,
    documentTitle: string,
    redaction: boolean,
    reviewState: string | null,
    nextRequest: object
  ) => {
    // @ts-ignore: pccViewer is defined in `prizmdoc/viewer.js`.
    const viewerControl = $(selector).pccViewer({
      nextRequest,
      annotationsMode: 'LayeredAnnotations',
      commentsPanelMode: 'full',
      documentID: viewingSessionId,
      documentDisplayName: DOMPurify.sanitize(documentTitle),
      imageHandlerUrl: getPrizmdocBaseUrl(),
      immediateActionMenuMode: 'on',
      immediateActionMenuActionsFilter: {
        comment: false,
        select: true,
        copy: true,
        highlight: false,
        redact: true,
        hyperlink: false,
        strikethrough: false,
        cancel: true,
        delete: true
      },
      language: window.viewerCustomizations.languages['en-US'],
      template: window.viewerCustomizations.template,
      icons: window.viewerCustomizations.icons,
      // icons: 'fake',
      // language: i18nData,
      predefinedSearch: regexes,
      redactionReasons,
      signatureCategories: 'Signature,Initials,Title',
      uiElements: helpers.buildUiElements(
        reviewState,
        redaction,

        nextRequest['rapidReview']
      ),
      viewerAssetsPath: getViewerAssetsPath()
    }).viewerControl
    if (redaction) {
      showRedactionTab(selector)
    }
    return viewerControl
  }
}
